import React, { useEffect, useMemo, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import {
    Box,
    Checkbox,
    Divider,
    Grid,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from './ChartingDefaults.redux';
import DashboardCard from 'components/DashboardCard';
import { AutoFixHigh } from '@mui/icons-material';
import DraggableCodeCard from 'components/DraggableCodeCard';
import { useDebounce } from '@uidotdev/usehooks';
import { List, Square, StarBorderOutlined } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import Info from 'components/Info';

const grid = 8,
    getListStyle = (isDraggingOver) => ({
        background: undefined, //isDraggingOver ? '#c9d9e7' : undefined,
        padding: `${grid}px 0 ${grid}px 0`,
        width: '100%',
        maxHeight: 600,
        overflowY: 'auto',
    }),
    getItemStyle = (isDragging, draggableStyle, type) => {
        return {
            // some basic styles to make the items look a bit nicer
            userSelect: 'none',
            padding: `${grid}px 8px ${grid}px 0`,
            margin: `0`,
            // change background color if dragging
            //background: color,
            // styles we need to apply on draggables
            ...draggableStyle,
        };
    },
    move = (source, destination, droppableSource, droppableDestination) => {
        const sourceClone = Array.from(source);
        const destClone = Array.from(destination);
        const [removed] = sourceClone.splice(droppableSource.index, 1);

        destClone.splice(droppableDestination.index, 0, removed);

        const result = {};
        result[droppableSource.droppableId] = sourceClone;
        result[droppableDestination.droppableId] = destClone;

        return result;
    },
    reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
    };

function filterSymbols({ types = ['dx', 'tx', 'other'], searchString = '', symbols = [] }) {
    // No types, don't waste cycles
    if (types.length === 0) {
        return [];
    }

    const searchStringNormalized = searchString.trim().toLowerCase();

    // All types are included and there's nothing to search for. Don't waste cycles.
    if (!searchStringNormalized && types.length === 3) {
        return symbols;
    }

    const filterBySearchString = Boolean(searchStringNormalized);
    const filterByType = types.length < 3;

    return symbols.filter((symbol) => {
        let stringMatch = false;
        let typeMatch = false;

        if (filterBySearchString) {
            stringMatch = String(symbol.content).toLowerCase().includes(searchStringNormalized);
        }

        if (filterByType) {
            typeMatch = types.includes(symbol.symbol_category);
        }

        if (filterByType && filterBySearchString) {
            return stringMatch && typeMatch;
        }

        return stringMatch || typeMatch;
    });
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const DefaultCodes = () => {
    const dispatch = useDispatch(),
        symbols = useSelector((state) => state.chartingDefaults.symbols_full),
        quickSymbols = useSelector((state) => state.chartingDefaults.preferences.chart_quick_symbols),
        loading = useSelector((state) => state.chartingDefaults.loading),
        saving = useSelector((state) => state.chartingDefaults.saving);

    const [state, setState] = useState({
        items: [],
        selected: [],
    });
    const [typeFilter, setTypeFilter] = useState(['dx', 'tx', 'other']);
    const [stringFilter, setStringFilter] = useState('');
    const [filteredItems, setFilteredItems] = useState([]);
    // name-asc, name-desc, code-asc, code-desc
    const [sortBy, setSortBy] = React.useState('name-asc');
    const [customCodesSortBy, setCustomCodesSortBy] = React.useState('custom');

    const stringFilterDebounced = useDebounce(stringFilter, 500);
    const typeFilterString = useMemo(() => {
        return typeFilter
            .map((filteredType) => filteredType.charAt(0).toUpperCase() + String(filteredType).slice(1))
            .join(', ');
    }, [typeFilter]);

    const getList = (id) => (id === 'allCodes' ? filteredItems : state.selected);

    const handleTypeFilterChange = (e) => {
        if (!e.target.value.length) {
            enqueueSnackbar('You must leave at least one type selected', {
                variant: 'warning',
                autoHideDuration: 3000,
            });
            return false;
        }

        setTypeFilter(e.target.value);
    };

    const sortDefaultCodes = (e) => {
        const sortOption = e.target.value;
        setCustomCodesSortBy(sortOption);

        if (sortOption === 'custom') {
            return true;
        }

        const symbolMap = symbols.reduce((accumulator, current) => {
            accumulator[current.symbol_id] = current;
            return accumulator;
        }, {});

        const quickSymbolsFull = quickSymbols
            .map((symbolID) => symbolMap[symbolID])
            .filter((symbol) => Boolean(symbol));

        if (sortOption === 'name-desc') {
            quickSymbolsFull.sort((a, b) => b.symbol_name.localeCompare(a.symbol_name));
        } else if (sortOption === 'code-desc') {
            quickSymbolsFull.sort((a, b) => b.symbol_abbreviation.localeCompare(a.symbol_abbreviation));
        } else if (sortOption === 'code-asc') {
            quickSymbolsFull.sort((a, b) => a.symbol_abbreviation.localeCompare(b.symbol_abbreviation));
        } else if (sortOption === 'name-asc') {
            quickSymbolsFull.sort((a, b) => a.symbol_name.localeCompare(b.symbol_name));
        }

        dispatch(actions.saveQuickSymbols(quickSymbolsFull.map((row) => row.symbol_id)));
        setState((state) => ({
            ...state,
            selected: quickSymbolsFull.map((symbol) => {
                return {
                    id: `symbol-${symbol.symbol_id}`,
                    content: `${symbol.symbol_category} - ${symbol.symbol_name} (${symbol.symbol_abbreviation})`,
                    symbol_id: symbol.symbol_id,
                    symbol_category: symbol.symbol_category,
                    clinic_id: symbol.clinic_id,
                    code: symbol.symbol_abbreviation,
                    name: symbol.symbol_name,
                    type: symbol.symbol_chart_config?.type ?? null,
                };
            }),
        }));
    };

    const handleDragEnd = ({ source, destination }) => {
        if (!destination) {
            return;
        }

        if (source.droppableId === destination.droppableId) {
            const items = reorder(getList(source.droppableId), source.index, destination.index);

            let newState = source.droppableId === 'defaultCodes' ? { ...state, selected: items } : { ...state, items };

            dispatch(actions.saveQuickSymbols(newState.selected.map((row) => row.symbol_id)));
            setState(newState);
        } else {
            const result = move(getList(source.droppableId), getList(destination.droppableId), source, destination);

            let newState = {
                items: state.items,
                selected: result.defaultCodes,
            };

            dispatch(actions.saveQuickSymbols(newState.selected.map((row) => row.symbol_id)));
            setState(newState);
            setFilteredItems(result.allCodes);
        }

        setCustomCodesSortBy('custom');
    };

    useEffect(() => {
        if (symbols) {
            const symbolMap = symbols.reduce((accumulator, current) => {
                accumulator[current.symbol_id] = current;
                return accumulator;
            }, {});

            const items = symbols
                .filter((row) => !quickSymbols.includes(row.symbol_id))
                .map((row) => ({
                    id: `symbol-${row.symbol_id}`,
                    content: `${row.symbol_name} (${row.symbol_abbreviation})`,
                    code: row.symbol_abbreviation,
                    name: row.symbol_name,
                    symbol_id: row.symbol_id,
                    symbol_category: row.symbol_category,
                    clinic_id: row.clinic_id,
                    type: row.symbol_chart_config?.type ?? null,
                }));

            if (sortBy === 'name-desc') {
                items.sort((a, b) => b.name.localeCompare(a.name));
            } else if (sortBy === 'code-desc') {
                items.sort((a, b) => b.code.localeCompare(a.code));
            } else if (sortBy === 'code-asc') {
                items.sort((a, b) => a.code.localeCompare(b.code));
            } else {
                items.sort((a, b) => a.name.localeCompare(b.name));
            }

            setState({
                items,
                selected: quickSymbols.map((id) => {
                    let symbol = symbolMap[id];

                    return {
                        id: `symbol-${symbol.symbol_id}`,
                        content: `${symbol.symbol_name} (${symbol.symbol_abbreviation})`,
                        symbol_id: symbol.symbol_id,
                        symbol_category: symbol.symbol_category,
                        clinic_id: symbol.clinic_id,
                        code: symbol.symbol_abbreviation,
                        name: symbol.symbol_name,
                        type: symbol.symbol_chart_config?.type ?? null,
                    };
                }),
            });
        }
    }, [setState, symbols, quickSymbols, sortBy]);

    useEffect(() => {
        setFilteredItems(
            filterSymbols({
                types: typeFilter,
                searchString: stringFilterDebounced,
                symbols: state.items,
            })
        );
    }, [state.items, typeFilter, stringFilterDebounced, setFilteredItems]);

    useEffect(() => {
        dispatch(actions.loadDefaults());
    }, [dispatch]);

    if (loading) {
        return null;
    }

    return (
        <DashboardCard color="orange" icon={<AutoFixHigh />} topContent="Default Charting Codes" size="xl">
            <Paper variant="outlined" sx={{ padding: 2, marginBottom: 2, marginTop: 1, background: '#f1f1f1' }}>
                <Typography>
                    Default Codes are the codes that appear when clicking the "Dx/Tx" button on the charting page. Drag
                    and drop your most frequently used codes from the list on the right into the list on the left for
                    easy access while charting.
                </Typography>
            </Paper>
            <Box sx={{ maxWidth: '1000px' }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        height: '26px',
                        marginBottom: '12px',
                    }}
                >
                    <Box component="span" sx={{ marginRight: 1, fontWeight: 'bold' }}>
                        Legend:
                    </Box>
                    <Square sx={{ color: 'rgb(20,59,255)' }} /> Diagnostic
                    <Divider
                        orientation="vertical"
                        sx={{
                            marginLeft: 1,
                            marginRight: 1,
                        }}
                    />
                    <Square sx={{ color: 'rgb(255,0,0)' }} /> Treatment
                    <Divider
                        orientation="vertical"
                        sx={{
                            marginLeft: 1,
                            marginRight: 1,
                        }}
                    />
                    <Square sx={{ color: '#16a800' }} /> Other
                    <Divider
                        orientation="vertical"
                        sx={{
                            marginLeft: 1,
                            marginRight: 1,
                        }}
                    />
                    <StarBorderOutlined /> Custom code
                    <Divider
                        orientation="vertical"
                        sx={{
                            marginLeft: 1,
                            marginRight: 1,
                        }}
                    />
                    <List /> Multiple-choice code
                </Box>

                <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                        <div>
                            <div>
                                Sort by...
                                <Info sx={{ fontSize: '16px' }}>
                                    <div>
                                        Sorting your default codes list performs a one-time sort. Any changes you make
                                        after sorting will remain in the order you placed them until you sort again.
                                    </div>
                                </Info>
                            </div>
                            <Select value={customCodesSortBy} onChange={sortDefaultCodes} sx={{ minWidth: '200px' }}>
                                <MenuItem value="name-asc">Name (A-Z)</MenuItem>
                                <MenuItem value="name-desc">Name (Z-A)</MenuItem>
                                <MenuItem value="code-asc">Abbreviation (A-Z)</MenuItem>
                                <MenuItem value="code-desc">Abbreviation (Z-A)</MenuItem>
                                <MenuItem value="custom">Custom</MenuItem>
                            </Select>
                        </div>
                    </Grid>

                    <Grid size={{ xs: 6 }}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 'auto' }}>
                                <div>
                                    <div>Code type</div>
                                    <Select
                                        multiple
                                        value={typeFilter}
                                        renderValue={() => typeFilterString}
                                        onChange={handleTypeFilterChange}
                                        sx={{ minWidth: '200px' }}
                                    >
                                        <MenuItem value="dx">
                                            <Checkbox checked={typeFilter.includes('dx')} />
                                            <ListItemText primary="Diagnostics" />
                                        </MenuItem>
                                        <MenuItem value="tx">
                                            <Checkbox checked={typeFilter.includes('tx')} />
                                            <ListItemText primary="Treatments" />
                                        </MenuItem>
                                        <MenuItem value="other">
                                            <Checkbox checked={typeFilter.includes('other')} />
                                            <ListItemText primary="Other" />
                                        </MenuItem>
                                    </Select>
                                </div>
                            </Grid>

                            <Grid size={{ xs: 'auto' }}>
                                <div>
                                    <div>Sort by...</div>
                                    <Select
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                        sx={{ minWidth: '200px' }}
                                    >
                                        <MenuItem value="name-asc">Name (A-Z)</MenuItem>
                                        <MenuItem value="name-desc">Name (Z-A)</MenuItem>
                                        <MenuItem value="code-asc">Abbreviation (A-Z)</MenuItem>
                                        <MenuItem value="code-desc">Abbreviation (Z-A)</MenuItem>
                                    </Select>
                                </div>
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <div>
                                    <div>Search codes</div>
                                    <TextField
                                        placeholder="Type to search"
                                        onChange={(e) => setStringFilter(e.target.value)}
                                    />
                                </div>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

                <Divider sx={{ marginTop: 2, marginBottom: 2 }} />
                <Grid container spacing={2}>
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Grid size={{ xs: 6 }}>
                            <div>Current Defaults</div>
                            <Droppable droppableId="defaultCodes">
                                {(provided, snapshot) => (
                                    <div ref={provided.innerRef} style={getListStyle(snapshot.isDraggingOver)}>
                                        {state.selected.map((item, index) => (
                                            <Draggable key={item.id} draggableId={item.id} index={index}>
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        style={getItemStyle(
                                                            snapshot.isDragging,
                                                            provided.draggableProps.style,
                                                            item.symbol_category
                                                        )}
                                                    >
                                                        <DraggableCodeCard code={item} />
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </Grid>

                        <Grid size={{ xs: 6 }}>
                            <div>Available to add to defaults</div>
                            <Droppable droppableId="allCodes">
                                {(provided, snapshot) => (
                                    <div ref={provided.innerRef} style={getListStyle(snapshot.isDraggingOver)}>
                                        {filteredItems.map((item, index) => (
                                            <Draggable key={item.id} draggableId={item.id} index={index}>
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        style={getItemStyle(
                                                            snapshot.isDragging,
                                                            provided.draggableProps.style,
                                                            item.symbol_category
                                                        )}
                                                    >
                                                        <DraggableCodeCard code={item} />
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </Grid>
                    </DragDropContext>
                </Grid>
            </Box>
        </DashboardCard>
    );
};

export default DefaultCodes;
